import { AHText, AHTextFonts, formatName, Box } from "arkh";
import { graphql, useFragment } from "react-relay";

import { ListItemEmployees_employees$key } from "./__generated__/ListItemEmployees_employees.graphql";
import {
  OverlappingEmployeeAvatars,
  isViewerEmployee,
  prioritizeEmployees,
} from "./OverlappingEmployeeAvatars";

export type ListItemEmployeesProps = {
  employees: ListItemEmployees_employees$key;
  effectiveMaxEmployees: number;
  highlightViewer: boolean;
  currentEmployeeId: string | undefined;
  maxWidth?: number;
  nameDisplayMode: "last-only" | "all";
  textType?: "body" | "detail";
  spacing?: "xsmall" | "small";
};

export function ListItemEmployees(props: ListItemEmployeesProps) {
  const employees = useFragment(
    graphql`
      fragment ListItemEmployees_employees on Employee @relay(plural: true) {
        id
        name
        isLinkedToViewer
        workStatus
        ...Avatar_employee
      }
    `,
    props.employees
  );

  if (employees.length === 0) {
    return null;
  }

  const sorted = prioritizeEmployees(employees, props.currentEmployeeId);
  const mostRelevant = sorted[0];
  const highlightName = props.highlightViewer
    ? isViewerEmployee(mostRelevant, props.currentEmployeeId)
    : false;

  return (
    <Box style={{ flexDirection: "row", alignItems: "center" }}>
      <OverlappingEmployeeAvatars
        employees={employees}
        maxAvatars={props.effectiveMaxEmployees}
        highlightViewer={props.highlightViewer}
        currentEmployeeId={props.currentEmployeeId}
      />
      <Box
        spaceLeft={props.spacing ?? "small"}
        style={{
          maxWidth: props.maxWidth,
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        {props.nameDisplayMode === "all" ? (
          <AHText type={props.textType ?? "body"} color="text-secondary">
            {sorted
              .slice(0, props.effectiveMaxEmployees)
              .reverse()
              .map((e) => formatName(e.name ?? ""))
              .join(", ")}
            {sorted.length > props.effectiveMaxEmployees
              ? ` +${sorted.length - props.effectiveMaxEmployees}`
              : ""}
          </AHText>
        ) : (
          <AHText
            type={props.textType ?? "body"}
            font={
              (props.textType ?? "body") === "body"
                ? AHTextFonts.MEDIUM
                : undefined
            }
            color={highlightName ? "darkerText" : "text-secondary"}
            numberOfLines={1}
          >
            {formatName(mostRelevant?.name ?? "")}
          </AHText>
        )}
      </Box>
    </Box>
  );
}
